import React from 'react';
import styles from './RightPanel.module.scss';
import SuggestionsPanel from '../SuggestionsPanel/SuggestionsPanel';
import ReasoningPanel from '../ReasoningPanel/ReasoningPanel';
import Library from '../Library/Library';

import { SuggestedQuestion } from '../../../../../../../../store/dashboard/askAica/actions';
import CrossIcon from '../../../../../../../../assets/Svg/CrossIcon';
import { leftPanelCloseIcon, rightPanelCloseIcon } from '../../../../../../../../assets/hostedassets';
import { AskAicaFileRef } from '../../hooks/useAskAicaFileActions';
import { ASK_AICA_SURFACE, AskAicaSurface } from '../../context/AskAicaSurfaceContext';

interface RightPanelProps {
    mode: 'suggestions' | 'reasoning';
    onModeChange: (mode: 'suggestions' | 'reasoning') => void;
    onSuggestionClick?: (suggestion: SuggestedQuestion) => void;
    onCrossButtonClick?: () => void;
    /** Opens a generated file in the matching in-app viewer (docx/xlsx/pdf). */
    onOpenFile: (file: AskAicaFileRef) => void;
    /** Snapshots a generated file into Reports (docx/xlsx/txt). Absent when the reports.addToReports visibility key is off. */
    onAddFileToReports?: (file: AskAicaFileRef) => void;
    /** URL of the file currently being opened — forwarded to Library for the card spinner. */
    openingFileUrl?: string | null;
    /** Portfolio has no Library equivalent — it reads the leads-only
     * askAicaReducer slice directly (RECUR-27369), which the portfolio surface
     * never populates. Defaulting to 'leads' keeps existing callers unchanged;
     * 'portfolio' hides Library so a leads session's stale slice data can never
     * bleed into the portfolio panel. */
    surface?: AskAicaSurface;
    /** Owned by AskAicaV2, mirroring the conversation sidebar. Collapsed renders a narrow
        strip carrying only the expand control; the cross stays a separate Close action. */
    collapsed: boolean;
    onCollapsedChange: (collapsed: boolean) => void;
}

const RightPanel: React.FC<RightPanelProps> = ({
    mode,
    onModeChange,
    onSuggestionClick,
    onCrossButtonClick,
    onOpenFile,
    onAddFileToReports,
    openingFileUrl,
    surface = ASK_AICA_SURFACE.LEADS,
    collapsed,
    onCollapsedChange,
}) => {
    const isPortfolio = surface === ASK_AICA_SURFACE.PORTFOLIO;

    // Figma 32258:170630 — collapsed removes the rail entirely; the expand control and
    // the Close cross both move into the chat header (see LeftPanel).
    if (collapsed) {
        return null;
    }

    return (
        <div className={styles.RightPanel}>
            {/* Toggle Header */}
            <div className={styles.RightPanelHeader}>
                <button
                    type="button"
                    className={styles.CollapseBtn}
                    onClick={() => onCollapsedChange(true)}
                    title="Collapse reasoning panel"
                    aria-label="Collapse reasoning panel"
                >
                    <img src={rightPanelCloseIcon} alt="" width="18" height="18" />
                </button>
                {isPortfolio ? (
                    <div className={styles.ToggleButtons}>
                        <button className={`${styles.ToggleButton} ${styles.Active}`}>Reasoning</button>
                    </div>
                ) : (
                    <div className={styles.ToggleButtons}>
                        <button
                            className={`${styles.ToggleButton} ${mode === 'suggestions' ? styles.Active : ''}`}
                            onClick={() => onModeChange('suggestions')}
                        >
                            Suggestions
                        </button>
                        <button
                            className={`${styles.ToggleButton} ${mode === 'reasoning' ? styles.Active : ''}`}
                            onClick={() => onModeChange('reasoning')}
                        >
                            Reasoning
                        </button>
                    </div>
                )}
                <button
                    type="button"
                    className={styles.CrossButton}
                    onClick={onCrossButtonClick}
                    aria-label="Close Ask AICA"
                    data-testid="ask-aica-close-button"
                >
                    <CrossIcon colour="#000000" />
                </button>
            </div>

            {/* Panel Content */}
            <div className={styles.RightPanelContent}>
                {isPortfolio ? (
                    <ReasoningPanel />
                ) : mode === 'suggestions' ? (
                    <SuggestionsPanel onSuggestionClick={onSuggestionClick} />
                ) : (
                    <ReasoningPanel />
                )}
            </div>

            {/* Library — leads only (see surface prop doc above); visible in both Suggestions and Reasoning tabs */}
            {!isPortfolio && <Library onOpenFile={onOpenFile} onAddFileToReports={onAddFileToReports} openingFileUrl={openingFileUrl} />}
        </div>
    );
};

export default RightPanel;
